import { createFileRoute, notFound } from "@tanstack/react-router";
import { AnimatePresence, motion, useMotionValue } from "motion/react";
import { useLayoutEffect, useState } from "react";
import { SwipeScreenStack } from "../-components/SwipeScreenStack";
import { IntroBackButton } from "./-components/IntroBackButton";
import { IntroDesktopActions } from "./-components/IntroDesktopActions";
import { Slide } from "./-components/Slide";
import { SwipeIndicator } from "./-components/SwipeIndicator";
import { VideoBackground } from "./-components/VideoBackground";
import { introConfigs } from "./-config";
import { IntroButtonPendingProvider } from "./-context/IntroButtonPendingContext";
import { useIsDesktopIntro } from "./-hooks/useIsDesktopIntro";

const DESKTOP_SLIDE_TRAVEL_PX = 40;

export const Route = createFileRoute("/intro/")({
    component: RouteComponent,

    head: ({ match }) => {
        const { tenant, locale } = match.search;
        return {
            meta: [
                {
                    title: `Intro - ${tenant} ${locale}`,
                },
            ],
        };
    },

    beforeLoad: () => {
        document.documentElement.style.setProperty(
            "--color-body-top",
            "var(--color-background-start)",
        );
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            "var(--color-background-start)",
        );
    },

    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const { tenant } = Route.useSearch();
    const config = introConfigs[tenant] ?? introConfigs.albertine;
    const PinnedElement = config.PinnedElement;
    const [currentSlide, setCurrentSlide] = useState(0);
    const dragY = useMotionValue(0);
    const isDesktop = useIsDesktopIntro();
    const slideTravelPx = isDesktop ? DESKTOP_SLIDE_TRAVEL_PX : undefined;

    useLayoutEffect(() => {
        if (!PinnedElement) {
            return;
        }

        function reserveCollageSpace() {
            const copies = document.querySelectorAll("[data-intro-copy]");
            let copyOffset = Number.POSITIVE_INFINITY;

            for (const copy of copies) {
                const frame = copy.parentElement;
                if (!frame) {
                    continue;
                }

                const copyTop =
                    copy.getBoundingClientRect().top -
                    frame.getBoundingClientRect().top;
                copyOffset = Math.min(copyOffset, copyTop);
            }

            if (!Number.isFinite(copyOffset)) {
                return;
            }

            document.documentElement.style.setProperty(
                "--intro-copy-offset",
                `${copyOffset}px`,
            );
        }

        reserveCollageSpace();
        window.addEventListener("resize", reserveCollageSpace);

        return () => {
            window.removeEventListener("resize", reserveCollageSpace);
            document.documentElement.style.removeProperty("--intro-copy-offset");
        };
    }, [PinnedElement]);

    return (
        <IntroButtonPendingProvider>
            <main aria-label="Intro" className="relative h-full w-full">
                <SwipeScreenStack
                    disableDrag={isDesktop}
                    dragY={dragY}
                    onSlideChange={setCurrentSlide}
                    slides={config.slides}
                    renderMovingBackground={({
                        combinedY,
                        currentSlide,
                        index,
                        slide,
                    }) => {
                        const video = slide.backgroundVideo;

                        if (!video) {
                            return null;
                        }

                        return (
                            <VideoBackground
                                key={index}
                                combinedY={combinedY}
                                index={index}
                                currentSlide={currentSlide}
                                showGradientOverlay={index === 0}
                                slideTravelPx={slideTravelPx}
                                video={video}
                            />
                        );
                    }}
                    renderFixedElement={({ currentSlide }) => {
                        const FixedElement = config.FixedElement;
                        const showFixedElement =
                            FixedElement &&
                            !config.hideFixedElementInSlides?.includes(currentSlide);

                        return (
                            <AnimatePresence>
                                {showFixedElement && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="pointer-events-none absolute inset-0 z-10"
                                    >
                                        <FixedElement />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        );
                    }}
                    renderStaticBackground={({ currentSlide }) => {
                        const BackgroundElement =
                            config.slides[currentSlide]?.BackgroundElement;

                        return (
                            <AnimatePresence>
                                {BackgroundElement && (
                                    <motion.div
                                        key={`bg-${currentSlide}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="pointer-events-none absolute inset-0"
                                    >
                                        <BackgroundElement />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        );
                    }}
                    renderSwipeIndicator={({ currentSlide, goToSlide, slideCount }) => (
                        <>
                            {isDesktop && (
                                <>
                                    <IntroBackButton
                                        isVisible={currentSlide > 0}
                                        onPress={() => goToSlide(currentSlide - 1)}
                                    />
                                    <IntroDesktopActions
                                        config={config}
                                        currentSlide={currentSlide}
                                        slideCount={slideCount}
                                        onContinue={() => goToSlide(currentSlide + 1)}
                                    />
                                </>
                            )}
                            <SwipeIndicator
                                currentSlide={currentSlide}
                                showPoweredByAlbertine={config.showPoweredByAlbertine}
                                showSwipeHint={!isDesktop}
                                totalSlides={slideCount}
                            />
                        </>
                    )}
                >
                    {({ combinedY, currentSlide, index, isLastSlide }) => (
                        <Slide
                            key={index}
                            combinedY={combinedY}
                            config={config}
                            currentSlide={currentSlide}
                            index={index}
                            isDesktop={isDesktop}
                            isLastSlide={isLastSlide}
                            slideTravelPx={slideTravelPx}
                        />
                    )}
                </SwipeScreenStack>
                {PinnedElement ? (
                    <div
                        className="pointer-events-none absolute inset-x-0 top-0 z-20"
                        style={{
                            height: "max(0px, calc(var(--intro-copy-offset, 100svh) - var(--spacing-40)))",
                        }}
                    >
                        <PinnedElement currentSlide={currentSlide} dragY={dragY} />
                    </div>
                ) : null}
            </main>
        </IntroButtonPendingProvider>
    );
}
